import { useTranslation } from 'react-i18next';
import { LabReferenceList } from '@/components/lab/LabObjectLink';
import { LabProvisionalStar, LabProvisionalStarLegend } from '@/components/lab/LabProvisionalStar';
import { LabReferenceInterval } from '@/components/lab/LabReferenceInterval';
import { LabValidatedValue } from '@/components/lab/LabValidatedValue';
import {
  codedCommentMeta,
  displayResultValue,
  displaySusceptibilityCell,
  formatLabDateTime,
  statusLabel,
  type IntervalFields,
} from '@/lib/lab/labDisplay';
import type {
  LabAntibiotic,
  LabCodedComment,
  LabInvestigation,
  LabInvestigationVersion,
  LabMicroscopy,
  LabObjectLink,
  LabOrganism,
  LabReference,
  LabResistance,
} from '@/lib/lab/labTypes';
import { cn } from '@/utils/cn';

type QuantFinding = {
  analysis?: { text?: string | null; code?: string | null; codeType?: string | null };
  operator?: string | null;
  value?: string | null;
  unit?: string | null;
  resultValidation?: string | null;
  referenceIntervalType?: string | null;
  referenceLower?: string | null;
  referenceUpper?: string | null;
  referenceIntervalText?: string | null;
  comments?: Array<string | null>;
  references?: LabReference[];
  interpretation?: LabCodedComment | null;
};

function findingCommentText(comments: Array<string | null> | undefined): string {
  return (comments ?? []).filter((c): c is string => Boolean(c)).join('\n');
}

function asCodedComment(raw: unknown): LabCodedComment | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as LabCodedComment;
  if (!rec.text && !rec.code && !rec.codeType) return null;
  return rec;
}

function quantitativeInterpretation(quantitative: Record<string, unknown> | null | undefined): LabCodedComment | null {
  return asCodedComment(quantitative?.interpretation);
}

function quantitativeComments(quantitative: Record<string, unknown> | null | undefined): string[] {
  const raw = quantitative?.comments;
  if (!Array.isArray(raw)) return [];
  return raw.filter((c): c is string => typeof c === 'string' && c.trim().length > 0);
}

function findingInterval(f: QuantFinding): IntervalFields {
  return {
    reference_interval_type: f.referenceIntervalType ?? null,
    reference_lower: f.referenceLower ?? null,
    reference_upper: f.referenceUpper ?? null,
    reference_interval_text: f.referenceIntervalText ?? null,
  };
}

function groupingText(groupings: unknown[]): string {
  return groupings
    .map((g) => {
      if (!g || typeof g !== 'object') return '';
      const rec = g as {
        headline?: string | null;
        types?: Array<{
          type?: string | null;
          details?: Array<{
            identification?: { text?: string | null };
            value?: { text?: string | null };
          }>;
        }>;
      };
      const details = (rec.types ?? [])
        .flatMap((t) =>
          (t.details ?? []).map((d) =>
            [t.type, d.identification?.text, d.value?.text].filter(Boolean).join(': '),
          ),
        )
        .join('; ');
      return [rec.headline, details].filter(Boolean).join(' — ');
    })
    .filter(Boolean)
    .join('\n');
}

export function LabMicrobiologyView({
  investigation,
  microscopy,
  organisms,
  antibiotics,
  resistance,
  versions,
  clinicId = '',
  objectLinks = [],
  consentGiven,
  replacedBy,
}: {
  investigation: LabInvestigation;
  microscopy: LabMicroscopy | undefined;
  organisms: LabOrganism[];
  antibiotics: LabAntibiotic[];
  resistance: LabResistance[];
  versions?: LabInvestigationVersion[];
  clinicId?: string;
  objectLinks?: LabObjectLink[];
  consentGiven?: boolean | null;
  replacedBy?: string | null;
}) {
  const { t } = useTranslation();
  const findings = Array.isArray(investigation.quantitative?.findings)
    ? (investigation.quantitative.findings as QuantFinding[])
    : [];
  const interpretation = quantitativeInterpretation(investigation.quantitative);
  const quantComments = quantitativeComments(investigation.quantitative);
  const corrected = investigation.is_corrected || investigation.result_status_code === 'svar_rettet';
  return (
    <div className="lab-section">
      <div className="lab-grid">
        <div className="lab-field">
          <dt>MDS / analyse</dt>
          <dd>
            <span className={cn(corrected && 'lab-value--corrected')}>
              {investigation.analysis_short_name || investigation.mic_analysis_code || '—'}
              {corrected ? ' R' : ''}
              <LabProvisionalStar code={investigation.result_status_code} />
            </span>
            <div className="text-sm text-ds-muted">
              {[investigation.analysis_mds_examination, investigation.analysis_mds_material, investigation.analysis_mds_location]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {investigation.mic_analysis_code ? (
              <div className="text-xs text-ds-muted">
                {investigation.mic_analysis_code}
                {investigation.analysis_code_type ? ` · ${investigation.analysis_code_type}` : ''}
                {investigation.analysis_code_responsible ? ` · ${investigation.analysis_code_responsible}` : ''}
              </div>
            ) : null}
            {investigation.examination_summary ? (
              <div className="mt-s2">{investigation.examination_summary}</div>
            ) : null}
          </dd>
        </div>
        <div className="lab-field">
          <dt>Svarproducent</dt>
          <dd>
            {investigation.producer_identifier || '—'}
            {investigation.producer_identifier_code ? (
              <div className="text-xs text-ds-muted">{investigation.producer_identifier_code}</div>
            ) : null}
            {investigation.examinator ? <div>{investigation.examinator}</div> : null}
          </dd>
        </div>
        <div className="lab-field">
          <dt>Svartid</dt>
          <dd>
            {formatLabDateTime(investigation.results_datetime)}
            {replacedBy ? <div className="mt-1 text-xs text-ds-muted">{replacedBy}</div> : null}
            {versions && versions.length > 0 ? (
              <ol className="mt-s2 space-y-1 text-xs text-ds-muted">
                {versions.map((v) => (
                  <li key={v.id}>
                    {formatLabDateTime(v.results_datetime)} · {v.examination_summary || statusLabel(v.result_status_code)}
                    {v.result_status_code === 'svar_rettet' ? ' R' : ''}
                  </li>
                ))}
              </ol>
            ) : null}
          </dd>
        </div>
      </div>

      {microscopy ? (
        <div>
          <h3>Mikroskopi</h3>
          <p className="font-medium">{microscopy.headline || '—'}</p>
          {microscopy.comments ? <p className="mt-s2 text-ds-text">{microscopy.comments}</p> : null}
          {microscopy.groupings.length > 0 ? (
            <pre className="mt-s2 whitespace-pre-wrap font-sans text-sm text-ds-text">
              {groupingText(microscopy.groupings)}
            </pre>
          ) : null}
        </div>
      ) : null}

      {organisms.length > 0 || investigation.culture_headline ? (
        <div>
          <h3>Dyrkning</h3>
          {investigation.culture_headline ? <p className="font-medium">{investigation.culture_headline}</p> : null}
          <table className="lab-table">
            <thead>
              <tr>
                <th>Mikroorganisme</th>
                <th>Lokalkode</th>
                <th>Vækst</th>
                <th>Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {organisms.map((org) => (
                <tr key={org.id}>
                  <td>{org.identification_text || '—'}</td>
                  <td>
                    {[org.code, org.code_type, org.code_responsible].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td>{org.growth_value || '—'}</td>
                  <td>{org.species_comment || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {investigation.culture_comments ? (
            <p className="mt-s2 text-sm text-ds-muted">{investigation.culture_comments}</p>
          ) : null}
        </div>
      ) : null}

      {investigation.pattern_headline || (antibiotics.length > 0 && resistance.length > 0) ? (
        <div>
          <h3>Resistens</h3>
          {investigation.pattern_headline ? (
            <p className="font-medium">{investigation.pattern_headline}</p>
          ) : null}
          <table className="lab-table">
            <thead>
              <tr>
                <th>Organisme</th>
                {antibiotics.map((ab) => (
                  <th key={ab.id}>{ab.name || `#${ab.ordinal}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {organisms.map((org) => (
                <tr key={org.id}>
                  <td>{org.identification_text || `#${org.ordinal}`}</td>
                  {antibiotics.map((ab) => {
                    const cell = resistance.find(
                      (r) => r.ref_microorganism === org.ordinal && r.ref_antibiotic === ab.ordinal,
                    );
                    return (
                      <td key={`${org.id}-${ab.id}`}>
                        {displaySusceptibilityCell(cell ?? {})}
                        <LabReferenceList
                          refs={cell?.susceptibility_references}
                          clinicId={clinicId}
                          reportId={investigation.report_id}
                          links={objectLinks}
                          consentGiven={consentGiven}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="lab-legend mt-s3">
            <span>S = sensitiv</span>
            <span>I = intermediær</span>
            <span>R = resistent</span>
          </div>
          {investigation.susceptibility_interpretation ? (
            <p className="mt-s2 text-sm">{investigation.susceptibility_interpretation}</p>
          ) : null}
        </div>
      ) : null}

      {findings.length > 0 || interpretation || quantComments.length > 0 ? (
        <div>
          <h3>{t('patients.lab.quantitative_findings')}</h3>
          {interpretation ? (
            <div>
              <p className="text-sm text-ds-muted">
                Tolkning{codedCommentMeta(interpretation) ? ` · ${codedCommentMeta(interpretation)}` : ''}
              </p>
              <p className="whitespace-pre-wrap">{interpretation.text || '—'}</p>
            </div>
          ) : null}
          {findings.length > 0 ? (
          <table className="lab-table">
            <thead>
              <tr>
                <th>Analyse</th>
                <th>Resultat</th>
                <th>Reference</th>
                <th>Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => {
                const interval = findingInterval(f);
                const comment = findingCommentText(f.comments);
                const findingInterp = asCodedComment(f.interpretation);
                return (
                  <tr key={i}>
                    <td>{f.analysis?.text || f.analysis?.code || '—'}</td>
                    <td>
                      {findingInterp?.text ? (
                        <div className="whitespace-pre-wrap">
                          {findingInterp.text}
                          {codedCommentMeta(findingInterp) ? (
                            <div className="text-xs text-ds-muted">{codedCommentMeta(findingInterp)}</div>
                          ) : null}
                        </div>
                      ) : null}
                      {f.value || investigation.result_status_code === 'proeve_modtaget' ? (
                        <>
                          <LabValidatedValue
                            validation={f.resultValidation}
                            interval={interval}
                            corrected={corrected}
                          >
                            {displayResultValue({
                              value: f.value ?? null,
                              operator: f.operator ?? null,
                              result_status_code: investigation.result_status_code,
                            })}
                          </LabValidatedValue>
                          {f.unit ? <span className="ml-1 text-ds-muted">{f.unit}</span> : null}
                        </>
                      ) : null}
                      {!findingInterp?.text && !f.value && investigation.result_status_code !== 'proeve_modtaget'
                        ? '—'
                        : null}
                      <LabReferenceList
                        refs={f.references}
                        clinicId={clinicId}
                        reportId={investigation.report_id}
                        links={objectLinks}
                        consentGiven={consentGiven}
                      />
                    </td>
                    <td>
                      <LabReferenceInterval {...interval} />
                    </td>
                    <td className="whitespace-pre-wrap">{comment || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          ) : null}
          {quantComments.length > 0 ? (
            <div className="mt-s2">
              {quantComments.map((text, index) => (
                <p key={index} className="whitespace-pre-wrap text-sm text-ds-text">
                  {text}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <LabProvisionalStarLegend codes={[investigation.result_status_code]} />
    </div>
  );
}
